import React, { useCallback, useEffect, useState } from 'react';
import { Search, BarChart3, Megaphone, Plug, ExternalLink, RefreshCw, ChevronDown } from 'lucide-react';
import { OsCard, Skeleton } from '../theme';

/* Admin Growth Panel — Search Console, GA4 & Google Ads at company level.
   Raw Google errors never reach this UI: the backend maps them to product states. */

const API = process.env.REACT_APP_BACKEND_URL;

const PRODUCTS = [
  { key: 'gsc', icon: Search, name: 'Search Console', color: '#4285F4' },
  { key: 'ga4', icon: BarChart3, name: 'Google Analytics', color: '#F9AB00' },
  { key: 'ads', icon: Megaphone, name: 'Google Ads', color: '#34A853' },
];

const fmt = (n) => (Number(n) || 0).toLocaleString();

const ago = (iso, isEs) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return isEs ? `hace ${m} min` : `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 48) return isEs ? `hace ${h} h` : `${h}h ago`;
  return isEs ? `hace ${Math.round(h / 24)} días` : `${Math.round(h / 24)}d ago`;
};

function Metric({ label, value, suffix = '' }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{label}</p>
      <p className="text-[19px] font-black tabular-nums leading-tight" style={{ color: 'var(--os-ink)' }}>{value}{suffix}</p>
    </div>
  );
}

function ConnectBtn({ product, isEs, label }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/google-suite/start?product=${product}`, { credentials: 'include' });
      const j = await r.json();
      if (j.url) window.location.href = j.url;
      else setBusy(false);
    } catch { setBusy(false); }
  };
  return (
    <button onClick={connect} disabled={busy} data-testid={`google-connect-${product}`}
      className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-bold text-white transition-all hover:opacity-90 disabled:opacity-50"
      style={{ background: 'var(--os-accent)' }}>
      <Plug className="w-3.5 h-3.5" /> {busy ? '…' : (label || (isEs ? 'Conectar' : 'Connect'))}
    </button>
  );
}

/* Maps a backend product-state payload to friendly copy + actions + admin diagnostic. */
function StateNotice({ d, product, isEs, onRetry }) {
  const msg = isEs ? d.message_es : d.message_en;
  const actions = d.actions || [];
  return (
    <div className="mt-3" data-testid={`google-state-${product}`}>
      <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--os-ink-soft)' }}>{msg}</p>
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        {actions.includes('retry') && (
          <button onClick={onRetry} data-testid={`google-retry-${product}`}
            className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[11.5px] font-bold transition-colors"
            style={{ border: '1px solid var(--os-border)', color: 'var(--os-ink)' }}>
            <RefreshCw className="w-3 h-3" /> {isEs ? 'Reintentar' : 'Retry'}
          </button>
        )}
        {actions.includes('refresh_status') && (
          <button onClick={onRetry} className="text-[11.5px] font-semibold underline underline-offset-2"
            style={{ color: 'var(--os-ink-soft)' }}>{isEs ? 'Actualizar estado' : 'Refresh status'}</button>
        )}
        {(actions.includes('reconnect') || actions.includes('review_access')) && (
          <ConnectBtn product={product} isEs={isEs} label={isEs ? 'Reconectar Google' : 'Reconnect Google'} />
        )}
      </div>
      {d.retry_in_min != null && actions.includes('retry') && (
        <p className="mt-2 text-[10.5px]" style={{ color: 'var(--os-ink-faint)' }}>
          {isEs ? `Reintento automático en ~${d.retry_in_min} min` : `Automatic retry in ~${d.retry_in_min} min`}
        </p>
      )}
      {d.diagnostic && (
        <details className="mt-2 group">
          <summary className="cursor-pointer list-none inline-flex items-center gap-1 text-[10.5px] font-semibold"
            style={{ color: 'var(--os-ink-faint)' }} data-testid={`google-diagnostic-${product}`}>
            <ChevronDown className="w-3 h-3 transition-transform group-open:rotate-180" />
            {isEs ? 'Diagnóstico técnico (admin)' : 'Technical diagnostic (admin)'}
          </summary>
          <pre className="mt-1.5 rounded-lg p-2 text-[10px] whitespace-pre-wrap break-all"
            style={{ background: 'var(--os-surface-2, rgba(0,0,0,0.04))', color: 'var(--os-ink-soft)' }}>
            {d.diagnostic}
          </pre>
        </details>
      )}
    </div>
  );
}

function StaleBanner({ d, isEs }) {
  return (
    <p className="mb-2 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold"
      style={{ background: 'rgba(245,158,11,0.12)', color: '#b45309' }} data-testid="google-stale-banner">
      {isEs
        ? `Última actualización correcta ${ago(d.last_good_at, true)} — Google no responde ahora mismo, reintentamos automáticamente.`
        : `Last successfully updated ${ago(d.last_good_at, false)} — Google is not responding right now, we retry automatically.`}
    </p>
  );
}

function UpdatedAt({ iso, isEs, onRefresh }) {
  return (
    <p className="mt-2.5 flex items-center gap-1.5 text-[10.5px]" style={{ color: 'var(--os-ink-faint)' }}>
      {isEs ? 'Datos actualizados' : 'Data updated'} {ago(iso, isEs)}
      <button onClick={onRefresh} title={isEs ? 'Actualizar ahora' : 'Refresh now'} className="hover:opacity-70">
        <RefreshCw className="w-2.5 h-2.5" />
      </button>
    </p>
  );
}

function GscData({ d, isEs }) {
  if (d.no_data) {
    return <p className="mt-3 text-[12px]" style={{ color: 'var(--os-ink-soft)' }}>
      {isEs ? 'Conexión correcta. Sin actividad en el periodo seleccionado.' : 'Connection successful. No activity was found for the selected period.'}</p>;
  }
  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        <Metric label="Clicks" value={fmt(d.totals.clicks)} />
        <Metric label={isEs ? 'Impresiones' : 'Impressions'} value={fmt(d.totals.impressions)} />
        <Metric label="CTR" value={d.totals.ctr} suffix="%" />
        <Metric label={isEs ? 'Posición' : 'Position'} value={d.totals.position} />
      </div>
      {d.top_queries?.length > 0 && (
        <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid var(--os-border-soft)' }}>
          <p className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Top búsquedas (28d)' : 'Top queries (28d)'}</p>
          {d.top_queries.slice(0, 4).map((q) => (
            <div key={q.query} className="flex items-center justify-between text-[12px]">
              <span className="truncate mr-2" style={{ color: 'var(--os-ink-soft)' }}>{q.query}</span>
              <span className="tabular-nums font-bold shrink-0" style={{ color: 'var(--os-ink)' }}>{fmt(q.clicks)} clicks</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function Ga4Data({ d, isEs }) {
  if (d.no_data) {
    return <p className="mt-3 text-[12px]" style={{ color: 'var(--os-ink-soft)' }}>
      {isEs ? 'Conexión correcta. Sin actividad en el periodo seleccionado.' : 'Connection successful. No activity was found for the selected period.'}</p>;
  }
  return (
    <>
      <div className="grid grid-cols-4 gap-2">
        <Metric label={isEs ? 'Usuarios' : 'Users'} value={fmt(d.totals.active_users)} />
        <Metric label={isEs ? 'Sesiones' : 'Sessions'} value={fmt(d.totals.sessions)} />
        <Metric label={isEs ? 'Vistas' : 'Pageviews'} value={fmt(d.totals.pageviews)} />
        <Metric label={isEs ? 'Eventos clave' : 'Key events'} value={fmt(d.totals.key_events)} />
      </div>
      {d.channels?.length > 0 && (
        <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid var(--os-border-soft)' }}>
          <p className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>{isEs ? 'Canales (28d)' : 'Channels (28d)'}</p>
          {d.channels.slice(0, 4).map((c) => (
            <div key={c.channel} className="flex items-center justify-between text-[12px]">
              <span className="truncate mr-2" style={{ color: 'var(--os-ink-soft)' }}>{c.channel}</span>
              <span className="tabular-nums font-bold shrink-0" style={{ color: 'var(--os-ink)' }}>{fmt(c.sessions)} {isEs ? 'ses.' : 'sess.'}</span>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function SummaryBody({ product, isEs }) {
  const [d, setD] = useState(null);
  const load = useCallback((refresh = false) => {
    if (refresh) setD(null);
    fetch(`${API}/api/admin/google-suite/${product}/summary${refresh ? '?refresh=true' : ''}`, { credentials: 'include' })
      .then((r) => r.json()).then(setD)
      .catch(() => setD({ status: 'google_unavailable', message_es: 'Los datos de Google no están disponibles temporalmente. KITXENS reintentará automáticamente.', message_en: 'Google data is temporarily unavailable. KITXENS will retry automatically.', actions: ['retry'] }));
  }, [product]);
  useEffect(() => { load(); }, [load]);

  if (!d) return <div className="mt-3 space-y-2"><Skeleton className="h-10" /><Skeleton className="h-16" /></div>;

  const Data = product === 'gsc' ? GscData : Ga4Data;
  if (d.status === 'ok') {
    return (
      <div className="mt-3" data-testid={`${product}-data`}>
        <Data d={d} isEs={isEs} />
        <UpdatedAt iso={d.fetched_at} isEs={isEs} onRefresh={() => load(true)} />
      </div>
    );
  }
  // failed but we have last-known-good data → keep showing it, clearly marked stale
  if (d.stale && d.data) {
    return (
      <div className="mt-3" data-testid={`${product}-data-stale`}>
        <StaleBanner d={d} isEs={isEs} />
        <Data d={d.data} isEs={isEs} />
        <StateNotice d={{ ...d, stale: undefined }} product={product} isEs={isEs} onRetry={() => load(true)} />
      </div>
    );
  }
  return <StateNotice d={d} product={product} isEs={isEs} onRetry={() => load(true)} />;
}

function AdsBody({ status, isEs }) {
  const [perf, setPerf] = useState(null);
  useEffect(() => {
    if (status !== 'connected_pending_token' && status !== 'ok' && status !== 'connected') return;
    fetch(`${API}/api/admin/google-suite/ads/performance`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setPerf).catch(() => {});
  }, [status]);
  if (status === 'connected_pending_token') {
    const CAP_LABELS = isEs
      ? ['Rendimiento de campañas', 'Inversión', 'Impresiones', 'Clics', 'Conversiones', 'Coste/conv.', 'ROAS', 'Términos de búsqueda', 'Salud de campañas', 'Oportunidades de presupuesto', 'Atribución a CRM']
      : ['Campaign performance', 'Spend', 'Impressions', 'Clicks', 'Conversions', 'Cost/conv.', 'ROAS', 'Search terms', 'Campaign health', 'Budget opportunities', 'CRM attribution'];
    const attr = perf?.attribution;
    return (
      <div className="mt-3" data-testid="ads-pending">
        <p className="text-[12px] font-bold" style={{ color: 'var(--os-ink)' }}>
          ✓ {isEs ? 'Conectado — pendiente de aprobación del developer token de Google Ads' : 'Connected — awaiting Google Ads developer token approval'}
        </p>
        <p className="mt-1 text-[11.5px] leading-relaxed" style={{ color: 'var(--os-ink-soft)' }}>
          {isEs ? 'Todo el reporting está construido y se activa solo en cuanto Google apruebe el token:' : 'All reporting is built and lights up automatically the moment Google approves the token:'}
        </p>
        <div className="mt-2 flex flex-wrap gap-1" data-testid="ads-capabilities">
          {CAP_LABELS.map((c) => (
            <span key={c} className="rounded-full border px-2 py-0.5 text-[9.5px] font-bold"
              style={{ borderColor: 'var(--os-line)', color: 'var(--os-ink-faint)' }}>{c}</span>
          ))}
        </div>
        {attr && (
          <p className="mt-2.5 text-[11.5px]" style={{ color: 'var(--os-ink-soft)' }} data-testid="ads-attribution">
            {isEs
              ? <>Atribución ya activa (30d, utm/gclid reales): <b style={{ color: 'var(--os-ink)' }}>{attr.attributed_visits}</b> visitas · <b style={{ color: 'var(--os-ink)' }}>{attr.attributed_conversions}</b> conversiones desde Google Ads.</>
              : <>Attribution already live (30d, real utm/gclid): <b style={{ color: 'var(--os-ink)' }}>{attr.attributed_visits}</b> visits · <b style={{ color: 'var(--os-ink)' }}>{attr.attributed_conversions}</b> conversions from Google Ads.</>}
          </p>
        )}
      </div>
    );
  }
  return <p className="mt-3 text-[12px]" style={{ color: 'var(--os-ink-soft)' }}>{isEs ? 'Conecta tu cuenta de Google Ads para ver inversión y campañas aquí.' : 'Connect your Google Ads account to see spend and campaigns here.'}</p>;
}

export default function GoogleGrowthPanel({ isEs }) {
  const [status, setStatus] = useState(null);
  useEffect(() => {
    const fallback = { gsc: { status: 'not_connected' }, ga4: { status: 'not_connected' }, ads: { status: 'not_connected' } };
    fetch(`${API}/api/admin/google-suite/status`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : fallback)).then(setStatus).catch(() => setStatus(fallback));
  }, []);
  return (
    <section className="mt-6" data-testid="google-growth-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: 'var(--os-ink-faint)' }}>
          {isEs ? 'Crecimiento · Google Growth Suite' : 'Growth · Google Growth Suite'}
        </p>
        <div className="flex items-center gap-3">
          <a href="/admin/ai-os/growth" className="text-[11px] font-bold underline underline-offset-2" style={{ color: 'var(--os-accent)' }} data-testid="open-growth-center">
            {isEs ? 'Abrir Growth Center →' : 'Open Growth Center →'}
          </a>
          <button onClick={() => window.location.reload()} className="inline-flex items-center gap-1 text-[11px] font-semibold transition-colors"
            style={{ color: 'var(--os-ink-faint)' }} data-testid="google-refresh" title={isEs ? 'Actualizar' : 'Refresh'}>
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {PRODUCTS.map(({ key, icon: Icon, name, color }) => {
          const st = status?.[key]?.status || 'not_connected';
          const connected = st !== 'not_connected';
          return (
            <OsCard key={key} className="p-5" data-testid={`google-card-${key}`}>
              <div className="flex items-center gap-2.5">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: `${color}1c` }}>
                  <Icon className="w-4.5 h-4.5" style={{ color, width: 18, height: 18 }} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-[14px] font-bold" style={{ color: 'var(--os-ink)' }}>{name}</p>
                  <p className="text-[11px] truncate" style={{ color: connected ? '#34D399' : 'var(--os-ink-faint)' }}>
                    {connected ? `● ${isEs ? 'Conectado' : 'Connected'}${status[key].google_email ? ` · ${status[key].google_email}` : ''}` : `○ ${isEs ? 'Sin conectar' : 'Not connected'}`}
                  </p>
                </div>
                {connected && key !== 'ads' && (
                  <a href={key === 'gsc' ? 'https://search.google.com/search-console' : 'https://analytics.google.com'}
                    target="_blank" rel="noopener noreferrer" style={{ color: 'var(--os-ink-faint)' }}>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
              {!status ? <div className="mt-3"><Skeleton className="h-14" /></div>
                : !connected ? (
                  <>
                    <p className="mt-3 text-[12px] leading-relaxed" style={{ color: 'var(--os-ink-soft)' }}>
                      {key === 'gsc' && (isEs ? 'Búsquedas, clicks, impresiones y posición de kitxens.com en Google.' : 'Queries, clicks, impressions and position of kitxens.com on Google.')}
                      {key === 'ga4' && (isEs ? 'Usuarios, sesiones, páginas vistas y canales de adquisición.' : 'Users, sessions, pageviews and acquisition channels.')}
                      {key === 'ads' && (isEs ? 'Inversión, campañas y conversiones de Google Ads.' : 'Spend, campaigns and conversions from Google Ads.')}
                    </p>
                    <div className="mt-3"><ConnectBtn product={key} isEs={isEs} /></div>
                  </>
                ) : key === 'ads' ? <AdsBody status={st} isEs={isEs} />
                  : <SummaryBody product={key} isEs={isEs} />}
            </OsCard>
          );
        })}
      </div>
    </section>
  );
}
